import { createElement, Fragment, ReactNode } from "react";

type TagType = 'B' | 'I' | 'U' | 'QUOTE' | 'CODE';

interface TagNode {
    type: 'text' | 'tag';
    content?: string;
    tag?: TagType;
    children?: TagNode[];
}

/**
 * Обрабатывает переносы строк в тексте, заменяя их на <br /> элементы
 */
function processLineBreaks(text: string): ReactNode[] {
    if (!text) return [];
    
    // Нормализуем переносы: \r\n -> \n, затем обрабатываем \n и оставшиеся \r
    const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const parts = normalized.split('\n');
    
    const result: ReactNode[] = [];
    parts.forEach((part, index) => {
        if (index > 0) {
            result.push(createElement('br', { key: `br-${index}` }));
        }
        if (part) {
            result.push(part);
        }
    });
    
    return result;
}

/**
 * Парсит текст с тегами форматирования поддержки и возвращает React элементы
 * Поддерживает теги: <B>, <I>, <U>, <QUOTE>, <CODE>
 * Поддерживает переносы строк: \r\n, \n, \r
 */
export function renderSupportMessage(text: string): ReactNode {
    if (typeof text !== 'string') return text;
    
    // Если нет тегов, обрабатываем только переносы строк
    if (!/<(B|I|U|QUOTE|CODE)>/i.test(text)) {
        const withBreaks = processLineBreaks(text);
        if (withBreaks.length === 0) return null;
        if (withBreaks.length === 1) return withBreaks[0];
        return createElement(Fragment, {}, ...withBreaks);
    }

    const nodes = parseSupportTags(text);
    const rendered = renderNodes(nodes);
    
    // Если только один элемент, возвращаем его напрямую, иначе оборачиваем в Fragment
    if (rendered.length === 0) return null;
    if (rendered.length === 1) return rendered[0];
    return createElement(Fragment, {}, ...rendered);
}

/**
 * Парсит текст с тегами в дерево узлов (рекурсивный подход)
 * Обрабатывает вложенные теги, находя самый внешний тег и рекурсивно парся содержимое
 */
function parseSupportTags(text: string): TagNode[] {
    if (!text) return [];

    const nodes: TagNode[] = [];
    
    // Находим первый открывающий тег
    const openTagRegex = /<(B|I|U|QUOTE|CODE)>/i;
    let searchStart = 0;

    while (true) {
        const openMatch = text.slice(searchStart).search(openTagRegex);
        if (openMatch === -1) {
            // Нет больше открывающих тегов - добавляем оставшийся текст
            const remaining = text.slice(searchStart);
            if (remaining) {
                nodes.push({ type: 'text', content: remaining });
            }
            break;
        }

        const openIndex = searchStart + openMatch;
        const openTagMatch = text.slice(openIndex).match(openTagRegex);
        if (!openTagMatch) break;

        const tag = openTagMatch[1].toUpperCase() as TagType;
        const openTagLength = openTagMatch[0].length;
        const contentStart = openIndex + openTagLength;

        // Добавляем текст перед открывающим тегом
        if (openIndex > searchStart) {
            const textBefore = text.slice(searchStart, openIndex);
            if (textBefore) {
                nodes.push({ type: 'text', content: textBefore });
            }
        }

        // Ищем соответствующий закрывающий тег
        const closeTag = `</${tag}>`;
        let closeIndex = contentStart;
        let depth = 1;

        while (depth > 0 && closeIndex < text.length) {
            const nextOpen = text.indexOf(`<${tag}>`, closeIndex);
            const nextClose = text.indexOf(closeTag, closeIndex);

            if (nextClose === -1) {
                // Не нашли закрывающий тег - добавляем все как текст
                const remaining = text.slice(searchStart);
                if (remaining) {
                    nodes.push({ type: 'text', content: remaining });
                }
                return nodes.length > 0 ? nodes : [{ type: 'text', content: text }];
            }

            if (nextOpen !== -1 && nextOpen < nextClose) {
                // Нашли вложенный открывающий тег
                depth++;
                closeIndex = nextOpen + openTagLength;
            } else {
                // Нашли закрывающий тег
                depth--;
                if (depth === 0) {
                    // Это наш закрывающий тег
                    const innerText = text.slice(contentStart, nextClose);
                    const innerNodes = parseSupportTags(innerText);
                    
                    nodes.push({
                        type: 'tag',
                        tag,
                        children: innerNodes.length > 0 ? innerNodes : (innerText ? [{ type: 'text', content: innerText }] : []),
                    });

                    searchStart = nextClose + closeTag.length;
                    break;
                } else {
                    closeIndex = nextClose + closeTag.length;
                }
            }
        }

        if (depth > 0) {
            // Не нашли закрывающий тег - добавляем все как текст
            const remaining = text.slice(searchStart);
            if (remaining) {
                nodes.push({ type: 'text', content: remaining });
            }
            break;
        }
    }

    return nodes.length > 0 ? nodes : [{ type: 'text', content: text }];
}

/**
 * Рендерит дерево узлов в React элементы
 * @param nodes - узлы для рендеринга
 * @param keyPrefix - префикс для ключей React элементов
 * @param insideCode - флаг, указывающий, что мы внутри тега CODE (переносы строк не обрабатываются)
 */
function renderNodes(nodes: TagNode[], keyPrefix: string = '', insideCode: boolean = false): ReactNode[] {
    const result: ReactNode[] = [];
    
    nodes.forEach((node, index) => {
        const key = `${keyPrefix}-${index}`;
        
        if (node.type === 'text') {
            // В CODE переносы строк сохраняются как есть (обрабатываются через CSS)
            // В остальных случаях заменяем на <br />
            if (insideCode) {
                result.push(node.content || '');
            } else {
                const withBreaks = processLineBreaks(node.content || '');
                if (withBreaks.length > 0) {
                    if (withBreaks.length === 1) {
                        result.push(withBreaks[0]);
                    } else {
                        result.push(...withBreaks);
                    }
                }
            }
            return;
        }

        if (node.type === 'tag' && node.tag) {
            // Для CODE передаем флаг insideCode=true в дочерние узлы
            const isCodeTag = node.tag === 'CODE';
            const children = node.children 
                ? renderNodes(node.children, key, insideCode || isCodeTag)
                : [];

            switch (node.tag) {
                case 'B':
                    result.push(createElement('strong', { key }, children));
                    break;
                case 'I':
                    result.push(createElement('em', { key }, children));
                    break;
                case 'U':
                    result.push(createElement('u', { key }, children));
                    break;
                case 'QUOTE':
                    result.push(createElement('blockquote', { key, className: 'support-quote' }, children));
                    break;
                case 'CODE':
                    // CODE может быть как inline, так и block - используем pre для блочного отображения
                    // В CODE переносы строк обрабатываются через white-space: pre-wrap в CSS
                    result.push(createElement('pre', { key }, 
                        createElement('code', { className: 'support-code' }, children)
                    ));
                    break;
                default:
                    result.push(createElement('span', { key }, children));
            }
        }
    });
    
    return result;
}

